import SiteUnitCommandsPopup from '@components/Battery/SiteUnitCommandsPopup/SiteUnitCommandsPopup';
import { useWsSubscribe } from '@store/actionCreators/mqtt';
import storeConnector from '@store/storeConnector';
import { useRef } from 'react';

export const SiteUnitCommandsRTDataFetcher = ({
  isShownCommandsPopup,
  siteMeta,
  popupProps,
}) => {
  const units = siteMeta?.Units ?? [];
  const unitNames = siteMeta?.UnitNames ?? [];

  const COMMON_SITE_UNIT_FIELDS = [
    'AllowedMaxP',
    'AllowedMaxQ',
    'AllowedMinP',
    'AllowedMinQ',
    'Pset',
    'Qset',
    'DCDC_PCS_P',
    'BESS_MinP',
    'BESS_MaxP',
    'ITCBuffer',
    'DCDCRampRate',
  ];

  const SECONDARY_SITE_FIELDS = [
    'Secondary_AllowedMaxP',
    'Secondary_AllowedMaxQ',
    'Secondary_AllowedMinP',
    'Secondary_AllowedMinQ',
    'Secondary_Pset',
    'Secondary_Qset',
    'Secondary_LocRemCtl',
  ];

  const batteryConnectingStatusTable = useRef({});

  const unitTable = useRef(
    units.map((sn, idx) => ({
      SN: sn,
      UnitName: unitNames[idx],
    })),
  );

  const rackModeTable = useRef({});
  const siteRt = useRef({});

  useWsSubscribe({
    bms: {
      sns: units,
      fields: ['ConnectingStatus'],
      sourceDeviceIds: ['bms_1'],
      cb: (data, sn) =>
        (batteryConnectingStatusTable.current[sn] = data?.ConnectingStatus),
    },
    rack: {
      sns: units,
      fields: ['RackMode'],
      sourceDeviceIds: ['bms_1_rack_1'],
      cb: (data, sn) => {
        if (data?.RackMode) {
          let rackMode = data.RackMode;
          rackModeTable.current[sn] = rackMode;
        }
      },
    },
    unit: {
      sns: units,
      fields: [
        'RunMode',
        'InvStatus',
        'LocRemCtl',
        'ACBreaker',
        ...COMMON_SITE_UNIT_FIELDS,
      ],
      cb: (data, sn) => {
        const idx = units.indexOf(sn);

        if (idx === -1 || !unitTable.current[idx]) {
          return;
        }

        Object.assign(unitTable.current[idx], data);
      },
    },
    site: {
      fields: [
        'LocRemCtl',
        ...COMMON_SITE_UNIT_FIELDS,
        ...SECONDARY_SITE_FIELDS,
      ],
      cb: (data) => {
        if (!siteRt.current) {
          siteRt.current = data;
        } else {
          Object.assign(siteRt.current, data);
        }
      },
    },
  });

  if (isShownCommandsPopup) {
    const popupType = popupProps?.popupType;
    const sn = popupProps?.sn;

    if (!popupType) {
      return null;
    }

    let siteOrUnit;

    const idx = sn ? units.indexOf(sn) : -1;
    let UnitName = '';

    if (idx !== -1) {
      UnitName = unitNames[idx];
    }

    const childProps = {
      lotoTableRt: { [sn]: popupProps.lotoTableRt },
    };

    if (popupType === 'site') {
      siteOrUnit = siteRt.current;

      Object.assign(childProps, {
        LocRemCtl: siteOrUnit.LocRemCtl,
        unitTable: unitTable.current,
      });
    } else if (popupType === 'unit') {
      if (idx === -1 || !unitTable.current[idx]) {
        return null;
      }

      siteOrUnit = unitTable.current[idx];

      Object.assign(childProps, {
        rackModeArr: rackModeTable.current[sn],
        batteryConnectingStatus: batteryConnectingStatusTable.current[sn],
        runMode: siteOrUnit['RunMode'] || siteOrUnit['Run Mode'],
        UnitName,
        invStatus: siteOrUnit['InvStatus'] || siteOrUnit['Inv Status'],
        converterPowerValue: siteOrUnit['DCDC_PCS_P'],
        converterMinPower: siteOrUnit['BESS_MinP'],
        converterMaxPower: siteOrUnit['BESS_MaxP'],
        itcBuffer: siteOrUnit['ITCBuffer'],
        dcdcRampRate: siteOrUnit['DCDCRampRate'],
      });
    } else {
      return null;
    }

    COMMON_SITE_UNIT_FIELDS.forEach(
      (field) => (childProps[field] = siteOrUnit?.[field]),
    );

    if (
      popupType === 'site' &&
      siteMeta.Site_Commands_Enable_Secondary_Site_Power_Cmds
    ) {
      SECONDARY_SITE_FIELDS.forEach(
        (field) => (childProps[field] = siteOrUnit?.[field]),
      );
    }

    return <SiteUnitCommandsPopup {...childProps} />;
  }

  return null;
};

export default storeConnector(SiteUnitCommandsRTDataFetcher, {
  service: ['isShownCommandsPopup', 'popupProps'],
  config: ['siteMeta'],
});
